import React, {ReactElement, useEffect, useState} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {DeviceType} from '../../../incase-domain/common/DeviceType.enum';
import {ICalculatorField, IElementCalculator} from '../../../incase-domain/elements/calculator';
import {calculateResult, fetchCalculatorById} from '../../../service/calculator.service';
import CalculatorDisplay from './CalculatorDisplay';

interface CalculatorElementComponentProps {
    element: IElementCalculator;
    device?: DeviceType;
}

function CalculatorElementComponent({element, device = DeviceType.DESKTOP}: CalculatorElementComponentProps): ReactElement {
    const {t} = useTranslation();

    const [inputState, setInputState] = useState<{[key: string]: string}>({});
    const [result, setResult] = useState<number>();

    const {data: calculator} = useQuery(
        QUERY_KEYS.calculator.detail(element.calculatorId),
        () => fetchCalculatorById(element.calculatorId),
        {
            enabled: !!element.calculatorId,
            onSuccess: () => {
                setInputState({});
                setResult(undefined);
            },
        }
    );

    const {mutate: calculate} = useMutation({
        mutationFn: (inputs: Record<string, number | string>) => calculateResult(element.calculatorId, inputs),
        onSuccess: (data) => {
            setResult(data);
        },
    });

    const handleInputChange = (value: string, field: ICalculatorField): void => {
        let processedValue = value;

        if (field.type === 'number') {
            const numericRegex = /^-?\d*\.?\d*$/;

            if (numericRegex.test(value) || value === '') {
                processedValue = value;
            } else {
                return;
            }
        }

        setInputState((prevState) => ({
            ...prevState,
            [field.name]: processedValue,
        }));
    };

    useEffect(() => {
        if (!calculator) return;

        const allInputsPresent = Object.keys(inputState).length === calculator.fields.length;

        if (!allInputsPresent) {
            setResult(undefined);
            return;
        }

        if (!calculator.formula) {
            calculate(inputState);
            return;
        }

        let formulaWithValues = calculator.formula;

        calculator.fields.forEach((field) => {
            const value = inputState[field.name];
            if (value !== undefined) {
                formulaWithValues = formulaWithValues.replace(new RegExp(`\\b${field.name}\\b`, 'g'), value.toString());
            }
        });

        try {
            // eslint-disable-next-line no-new-func
            const computedResult = Function(`"use strict"; return (${formulaWithValues})`)();
            setResult(parseFloat(computedResult.toFixed(2)));
        } catch (error) {
            setResult(undefined);
        }
    }, [calculator, inputState, calculate]);

    if (!element.calculatorId || !calculator) {
        return <div className="c-calculator">{t('presEditPage.editSidebar.elements.calculator.notFound')}</div>;
    }

    return (
        <CalculatorDisplay
            calculator={calculator}
            inputState={inputState}
            result={result}
            onInputChange={handleInputChange}
            device={device}
        />
    );
}

export default CalculatorElementComponent;
